import { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { CbmImpl } from "~/modules/cbm";
import {
    checkChannelSid,
    checkFlexInstanceSid,
    checkInteractionSid,
    checkReservationExists,
    getReservationsByTask,
    handleGetParticipantsByTask
} from "~/modules/actions/ActionUtils";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { AccountConfigDataContainer } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigDataContainer/AccountConfigDataContainer";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";




export interface StartConversationTransferOptions {}






































export class StartConversationTransfer implements Action<Promise<void>> {
    


    readonly #taskSid: string;

    


    readonly #to: string;

    constructor(taskSid: string, to: string) {
        this.#taskSid = taskSid;
        this.#to = to;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const logger = getLogger(context)(LoggerName.Actions);

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const worker = taskRouter.worker;

        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        const accountConfigDataContainer = context.retrieveInstanceOf(
            ContextInstance.AccountConfigDataContainer,
            AccountConfigDataContainer
        );

        logger.debug(`StartConversationTransfer invoked with taskSid: ${this.#taskSid}`);

        const reservation = getReservationsByTask(worker, this.#taskSid)[0];

        checkReservationExists(reservation, this.#taskSid, {
            actionName: "StartConversationTransfer",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.StartConversationTransfer,
            extra: {
                taskSid: this.#taskSid,
                to: this.#to
            },
            logger
        });
        const task = reservation.task;

        const interactionSid = task.attributes.flexInteractionSid as string | undefined;

        const channelSid = task.attributes.flexInteractionChannelSid as string | undefined;

        checkInteractionSid(interactionSid, this.#taskSid, {
            actionName: "StartConversationTransfer",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.StartConversationTransfer,
            extra: {
                taskSid: this.#taskSid,
                to: this.#to
            },
            logger
        });

        checkChannelSid(channelSid, this.#taskSid, {
            actionName: "StartConversationTransfer",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.StartConversationTransfer,
            extra: {
                taskSid: this.#taskSid,
                to: this.#to
            },
            logger
        });

        let instanceSid = accountConfigDataContainer.get().flexInstanceSid;
        instanceSid = checkFlexInstanceSid(instanceSid, {
            actionName: "StartConversationTransfer",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.StartConversationTransfer,
            extra: {
                taskSid: this.#taskSid,
                to: this.#to,
                interactionSid: interactionSid!,
                channelSid: channelSid!
            },
            logger
        });

        const participants = await handleGetParticipantsByTask(cbm, task, {
            actionName: "StartConversationTransfer",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.StartConversationTransfer,
            extra: {
                taskSid: this.#taskSid,
                to: this.#to,
                interactionSid: interactionSid!,
                channelSid: channelSid!
            },
            logger
        });

        const workerParticipant = participants.find(
            (p) =>
                (String(p.type) === "agent" || String(p.type) === "supervisor") &&
                p.routingProperties?.workerSid === worker.workerSid
        );

        if (!workerParticipant) {
            const errorMsg = `StartConversationTransfer: Worker participant not found for task ${this.#taskSid}`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.StartConversationTransfer, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                to: this.#to,
                interactionSid: interactionSid!,
                channelSid: channelSid!
            });
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.WorkerParticipantNotFoundForTask,
                    { severity: ErrorSeverity.Error },
                    errorMsg
                )
            );
        }

        try {
            await cbm.transferChannel(
                instanceSid,
                interactionSid!,
                channelSid!,
                workerParticipant.participantSid,
                this.#to
            );

            sendWorkerTrackingEvent(EVENTS.StartConversationTransfer, analytics, worker, {
                taskSid: this.#taskSid,
                to: this.#to,
                interactionSid: interactionSid!,
                channelSid: channelSid!,
                workerParticipantSid: workerParticipant.participantSid
            });

            return Promise.resolve();
        } catch (error) {
            const errorMessage = toCauseMessage(error);
            if (errorMessage?.toLowerCase().includes("forbidden")) {
                const errorMsg = `StartConversationTransfer: Failed to start conversation transfer for task ${
                    this.#taskSid
                }, interactionSid ${interactionSid!}, channelSid ${channelSid!}. Conversation transfer might not be enabled in the configuration`;
                logger.error(errorMsg, error);
                sendWorkerTrackingEvent(EVENTS.StartConversationTransfer, analytics, worker, {
                    error: errorMsg,
                    taskSid: this.#taskSid,
                    to: this.#to,
                    interactionSid: interactionSid!,
                    channelSid: channelSid!,
                    workerParticipantSid: workerParticipant.participantSid
                });
                return Promise.reject(
                    createFlexSdkError(context)(
                        ErrorCode.ConversationTransferForbidden,
                        { severity: ErrorSeverity.Error },
                        errorMsg,
                        error
                    )
                );
            }

            const errorMsg = `StartConversationTransfer: Failed to start conversation transfer for task ${
                this.#taskSid
            }, interactionSid ${interactionSid!}, channelSid ${channelSid!}`;
            logger.error(errorMsg, error);
            sendWorkerTrackingEvent(EVENTS.StartConversationTransfer, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                to: this.#to,
                interactionSid: interactionSid!,
                channelSid: channelSid!,
                workerParticipantSid: workerParticipant.participantSid
            });

            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToStartConversationTransfer,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }
}
